import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Play, Pause, Plus, Trash2 } from "lucide-react";

const segments = [
  { k: "segue", label: "Segue", desc: "Personal & professional bests (5 min)", mins: 5 },
  { k: "scorecard_notes", label: "Scorecard", desc: "Review red metrics (5 min)", mins: 5 },
  { k: "headlines", label: "Headlines", desc: "Customer + team wins (5 min)", mins: 5 },
  { k: "todos_notes", label: "To-do review", desc: "Last week's to-dos (5 min)", mins: 5 },
  { k: "ids_notes", label: "IDS", desc: "Identify · Discuss · Solve (60 min)", mins: 60 },
  { k: "conclude", label: "Conclude", desc: "Recap + rating (5 min)", mins: 5 },
];

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [active, setActive] = useState(null);
  const [seconds, setSeconds] = useState(90 * 60);
  const [running, setRunning] = useState(false);
  const tick = useRef(null);

  const load = () => api.get("/meetings").then((r) => setMeetings(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (running) tick.current = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(tick.current);
  }, [running]);

  const newMeeting = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await api.post("/meetings", {
      date: today, attendees: [], segue: "", scorecard_notes: "", headlines: "",
      todos: [], issues_solved: [], conclude: "", rating: null,
    });
    setActive({ ...data, todos_notes: "", ids_notes: "" });
    setSeconds(90 * 60);
    load();
  };

  const save = async () => {
    if (!active) return;
    const payload = {
      date: active.date, attendees: active.attendees || [], segue: active.segue || "",
      scorecard_notes: active.scorecard_notes || "", headlines: active.headlines || "",
      todos: active.todos || [], issues_solved: active.issues_solved || [],
      conclude: active.conclude || "", rating: active.rating,
    };
    await api.put(`/meetings/${active.id}`, payload);
    toast.success("Meeting saved.");
    load();
  };

  const del = async (id) => { if (confirm("Delete this meeting?")) { await api.delete(`/meetings/${id}`); if (active?.id===id) setActive(null); load(); } };

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  if (active) {
    return (
      <div className="p-6 lg:p-8 max-w-5xl">
        <div className="flex items-center justify-between sticky top-0 bg-[#F9F9F8] py-3 z-10 border-b border-neutral-200 -mx-6 lg:-mx-8 px-6 lg:px-8">
          <div>
            <div className="mono text-xs uppercase text-neutral-500 tracking-widest">L10 Meeting · {active.date}</div>
            <h1 className="font-display text-2xl font-bold mt-1">Weekly Level 10</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="mono text-4xl tabular-nums" data-testid="meeting-timer">{mm}:{ss}</div>
            <Button variant="outline" onClick={() => setRunning((r) => !r)} className="rounded-sm" data-testid="meeting-timer-toggle">
              {running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" onClick={() => setActive(null)} className="rounded-sm" data-testid="meeting-back-btn">Close</Button>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {segments.map((s) => (
            <div key={s.k} className="border border-neutral-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold">{s.label}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{s.desc}</div>
                </div>
                <div className="mono text-xs text-neutral-500">{s.mins}m</div>
              </div>
              <Textarea
                rows={3}
                value={active[s.k] || ""}
                onChange={(e) => setActive({ ...active, [s.k]: e.target.value })}
                className="mt-3 rounded-sm"
                placeholder="Notes…"
                data-testid={`meeting-segment-${s.k}`}
              />
            </div>
          ))}
          <div className="border border-neutral-200 bg-white p-5">
            <Label>Meeting rating (1–10)</Label>
            <Input type="number" min={1} max={10} value={active.rating || ""} onChange={(e)=>setActive({...active, rating: Number(e.target.value)})} className="rounded-sm mt-3 w-32" data-testid="meeting-rating-input" />
          </div>
          <Button onClick={save} className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-sm btn-lift" data-testid="meeting-save-btn">Save Meeting</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-neutral-500">Module · Meetings</div>
          <h1 className="font-display text-4xl font-bold mt-2">L10 Weekly Meetings</h1>
          <p className="text-neutral-600 mt-2">Same agenda every week. 90 minutes. Start on time.</p>
        </div>
        <Button onClick={newMeeting} className="bg-[#E54D2E] hover:bg-[#C93E22] text-white rounded-sm btn-lift" data-testid="meetings-new-btn"><Plus className="h-4 w-4 mr-1" /> Start L10</Button>
      </div>

      <div className="mt-8 border border-neutral-200 bg-white">
        {meetings.length === 0 && <div className="p-12 text-center text-neutral-500">No meetings yet. Start your first L10 →</div>}
        {meetings.map((m) => (
          <div key={m.id} className="border-b border-neutral-100 last:border-b-0 p-4 flex items-center hover:bg-neutral-50 gap-4" data-testid={`meeting-row-${m.id}`}>
            <div className="mono text-sm w-32">{m.date}</div>
            <div className="flex-1 text-sm text-neutral-600 truncate">{m.headlines || "No headlines"}</div>
            <div className="mono text-sm text-neutral-500">{m.rating ? `★ ${m.rating}/10` : "—"}</div>
            <Button variant="outline" size="sm" onClick={() => setActive({ ...m, todos_notes: "", ids_notes: "" })} className="rounded-sm" data-testid={`meeting-open-${m.id}`}>Open</Button>
            <button onClick={() => del(m.id)} className="text-neutral-400 hover:text-red-600" data-testid={`meeting-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
